import { Flex, Skeleton, Text } from "@chakra-ui/react"
import React from "react"

import StatBox from "@/common/components/StatBox"
import type { KarmaStats } from "@/governance/helpers/karma"
import type { Account } from "@/query/graphql"

type AccountCardKarmaScoreProps = {
  hasKarmaScore: boolean | undefined
  karmaScores:
    | {
        [address: string]: KarmaStats
      }
    | undefined
  karmaScoresFetched:
    | {
        [address: string]: boolean
      }
    | undefined
  isLoadingKarmaScores: boolean | undefined
  account: Pick<Account, "bio" | "name" | "picture" | "address" | "twitter">
}

const AccountCardKarmaScore: React.FC<AccountCardKarmaScoreProps> = ({
  hasKarmaScore,
  karmaScores,
  karmaScoresFetched,
  isLoadingKarmaScores,
  account,
}) => {
  if (
    hasKarmaScore &&
    karmaScores &&
    karmaScoresFetched &&
    karmaScoresFetched[account.address.toLowerCase()] &&
    !karmaScores[account.address.toLowerCase()]
  ) {
    return (
      <Flex align="center" bg="gray.50" h="45px" justify="center">
        <Text color="gray.600" textStyle="body.bold.md">
          Karma score is not available
        </Text>
      </Flex>
    )
  }

  if (
    hasKarmaScore &&
    karmaScores &&
    !karmaScores[account.address.toLowerCase()] &&
    isLoadingKarmaScores
  ) {
    return <Skeleton height="45px" />
  }

  if (hasKarmaScore && karmaScores) {
    const stats = karmaScores[account.address.toLowerCase()]

    if (!stats) return null
    const data = {
      karmaScore: stats?.karmaScore,
      offChainVotesPct: stats?.offChainVotesPct,
      onChainVotesPct: stats?.onChainVotesPct,
      forumActivityScore: stats?.forumActivityScore,
    }
    const isLoading = !stats && isLoadingKarmaScores
    const isRowFull =
      Boolean(data.offChainVotesPct) && Boolean(data.forumActivityScore)

    return (
      <Flex
        gap={isRowFull ? "initial" : 4}
        justify={isRowFull ? "space-between" : "flex-start"}
        width="100%"
      >
        <StatBox
          alignItems="center"
          isLoading={isLoading}
          label="Karma Score"
          labelSize="10px"
          size="14px"
          tooltipText="Aggregate score calculated by Karma"
          value={data.karmaScore}
          whiteSpace="nowrap"
        />

        {!isLoading && data.offChainVotesPct ? (
          <StatBox
            alignItems="center"
            color={
              data.offChainVotesPct > 80
                ? "green.600"
                : data.offChainVotesPct >= 50
                  ? "yellow.600"
                  : "red.600"
            }
            isLoading={isLoading}
            label="Snapshot pct"
            labelSize="10px"
            size="14px"
            tooltipText="Voting score based on off-chain proposals (Snapshot)"
            value={`${data.offChainVotesPct}%`}
            whiteSpace="nowrap"
          />
        ) : null}

        <StatBox
          alignItems="center"
          color={
            data.onChainVotesPct > 80
              ? "green.600"
              : data.onChainVotesPct >= 50
                ? "yellow.600"
                : "red.600"
          }
          isLoading={isLoading}
          label="Onchain pct"
          labelSize="10px"
          size="14px"
          tooltipText="Voting score based on onchain proposals"
          value={`${data.onChainVotesPct}%`}
          whiteSpace="nowrap"
        />

        {!isLoading && data.forumActivityScore ? (
          <StatBox
            alignItems="center"
            isLoading={isLoading}
            label="Forum score"
            labelSize="10px"
            size="14px"
            tooltipText="Participation score based on Discourse"
            value={data.forumActivityScore}
            whiteSpace="nowrap"
          />
        ) : null}
      </Flex>
    )
  }

  return null
}

export default AccountCardKarmaScore
